import { useCallback } from 'react';

import {
    resolveEvaluationElementsTransitions,
    resolveReactivatedStatus,
} from './evaluationElementDeactivation';
import {
    EVALUATION_TYPE_ORDER,
    getSelectedEvaluationTypes,
    hasCycleEvaluationBeenSent,
    selectionIncludesFeedback360,
} from './evaluationSelection';
import { getPersistedTemplateId } from './templateOptions';
import {
    getEvaluationElementsBlockingMessage,
    getEvaluationElementsRemovalBlockingMessage,
    isDeletedTemplateImportFailure,
    TEMPLATE_NO_LONGER_AVAILABLE,
} from './validation';

import type { EvaluationElementsSelection } from './evaluationSelection';
import type { CreateCycleEvaluationForGroupVariables } from '@/api/cycle-evaluation-services/cycle-evaluation';
import type { UpdateCycleEvaluationGroupVariables } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import type { ImportTemplateParams } from '@/api/review-services/review-questionnaire/types';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import {
    CycleEvaluationStatus,
    EvaluationType,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleEvaluationGroupSteps } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { isCycleEvaluationGroupLaunched } from '@/navigation/CycleEvaluation/cycleEvaluationGroupLaunched';
import { buildCycleEvaluationGroupUpdatePayload } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { showWarningToast } from '@/plugins/Toast';

interface UseSaveEvaluationElementsParams {
    isReadOnly: boolean;
    selection: EvaluationElementsSelection;
    typesWithUnavailableTemplate: readonly EvaluationTypeValue[];
    typesWithUnresolvableTemplate: readonly EvaluationTypeValue[];
    group: CycleEvaluationGroup | undefined;
    cycleEvaluationGroupId: string | undefined;
    cycleEvaluationByType: Partial<Record<EvaluationTypeValue, CycleEvaluation>>;
    createCycleEvaluationForGroup: (
        params: CreateCycleEvaluationForGroupVariables,
    ) => Promise<CycleEvaluation>;
    updateCycleEvaluation: (evaluation: CycleEvaluation) => Promise<CycleEvaluation>;
    importTemplateInCycleQuestionnaire: (params: ImportTemplateParams) => Promise<unknown>;
    updateCycleEvaluationGroup: (params: UpdateCycleEvaluationGroupVariables) => Promise<unknown>;
    onTemplateImportFailed: (types: readonly EvaluationTypeValue[]) => void;
}

interface TemplateImportCandidate {
    type: EvaluationTypeValue;
    questionnaireId: string;
    templateId: string;
}

/**
 * Whether the selection on screen differs from what the cycle already holds: a type to attach,
 * wake or retire, another template for a checked type, or the organizational-values toggle.
 * Mirrors the writes `onNext` would make, so "unchanged" means "not one write would leave".
 */
const hasEvaluationElementsChanged = ({
    group,
    selection,
    cycleEvaluationByType,
}: {
    group: CycleEvaluationGroup;
    selection: EvaluationElementsSelection;
    cycleEvaluationByType: Partial<Record<EvaluationTypeValue, CycleEvaluation>>;
}): boolean => {
    const selectedTypes = getSelectedEvaluationTypes(selection);
    const { typesToCreate, typesToReactivate, typesToDeactivate } =
        resolveEvaluationElementsTransitions({ group, selectedTypes });

    if (typesToCreate.length > 0 || typesToReactivate.length > 0 || typesToDeactivate.length > 0) {
        return true;
    }

    const managerEvaluation = cycleEvaluationByType[EvaluationType.MANAGER_EVALUATION];

    if (
        selection.managerAssessment &&
        managerEvaluation &&
        (managerEvaluation.assessOrganizationalValues ?? false) !==
            selection.assessOrganizationalValues
    ) {
        return true;
    }

    return selectedTypes.some((type) => {
        const questionnaire = cycleEvaluationByType[type]?.cycleQuestionnaire;
        const chosenTemplateId = selection.templateIds[type];

        return (
            !!questionnaire?.id &&
            !!chosenTemplateId &&
            chosenTemplateId !== getPersistedTemplateId(questionnaire)
        );
    });
};

export const useSaveEvaluationElements = ({
    isReadOnly,
    selection,
    typesWithUnavailableTemplate,
    typesWithUnresolvableTemplate,
    group,
    cycleEvaluationGroupId,
    cycleEvaluationByType,
    createCycleEvaluationForGroup,
    updateCycleEvaluation,
    importTemplateInCycleQuestionnaire,
    updateCycleEvaluationGroup,
    onTemplateImportFailed,
}: UseSaveEvaluationElementsParams) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    return useCallback(async () => {
        if (isReadOnly) {
            return false;
        }

        const isLaunched = isCycleEvaluationGroupLaunched(group);

        // A launched cycle whose selection still reads what the server holds has nothing to
        // validate or write: "Next" merely moves on.
        if (
            group &&
            isLaunched &&
            !hasEvaluationElementsChanged({ group, selection, cycleEvaluationByType })
        ) {
            return true;
        }

        const blocking = getEvaluationElementsBlockingMessage({
            selection,
            typesWithUnavailableTemplate,
            typesWithUnresolvableTemplate,
        });

        if (blocking) {
            showWarningToast({ message: t(blocking) });

            return false;
        }

        if (!group || !cycleEvaluationGroupId) {
            return false;
        }

        const selectedTypes = getSelectedEvaluationTypes(selection);
        const { typesToCreate, typesToReactivate, typesToDeactivate } =
            resolveEvaluationElementsTransitions({ group, selectedTypes });

        const hasBeenSentByType: Partial<Record<EvaluationTypeValue, boolean>> = Object.fromEntries(
            EVALUATION_TYPE_ORDER.map((type) => [
                type,
                hasCycleEvaluationBeenSent(cycleEvaluationByType[type]),
            ]),
        );

        const removalBlocking = getEvaluationElementsRemovalBlockingMessage(
            typesToDeactivate,
            hasBeenSentByType,
        );

        if (removalBlocking) {
            showWarningToast({ message: t(removalBlocking) });

            return false;
        }

        const statusChanges: CycleEvaluation[] = [];
        const reactivatedStatus = resolveReactivatedStatus(group);

        for (const [type, status] of [
            ...typesToDeactivate.map((type) => [type, CycleEvaluationStatus.INACTIVE] as const),
            ...typesToReactivate.map((type) => [type, reactivatedStatus] as const),
        ]) {
            const evaluation = cycleEvaluationByType[type];

            if (!evaluation) {
                handleError({});

                return false;
            }

            statusChanges.push({ ...evaluation, status });
        }

        try {
            // Wrapped rather than passed by reference: `map` hands the callback the index, and a
            // mutation reads its second argument as an options object.
            await Promise.all(statusChanges.map((evaluation) => updateCycleEvaluation(evaluation)));

            const createdByType = new Map<EvaluationTypeValue, CycleEvaluation>();

            await Promise.all(
                typesToCreate.map(async (reviewType) => {
                    createdByType.set(
                        reviewType,
                        await createCycleEvaluationForGroup({ reviewType, cycleEvaluationGroupId }),
                    );
                }),
            );

            // "Assess organizational value" is persisted on the manager evaluation.
            const managerEvaluation =
                createdByType.get(EvaluationType.MANAGER_EVALUATION) ??
                cycleEvaluationByType[EvaluationType.MANAGER_EVALUATION];

            if (
                selection.managerAssessment &&
                managerEvaluation &&
                (managerEvaluation.assessOrganizationalValues ?? false) !==
                    selection.assessOrganizationalValues
            ) {
                await updateCycleEvaluation({
                    ...managerEvaluation,
                    assessOrganizationalValues: selection.assessOrganizationalValues,
                });
            }

            const templateImportCandidates: TemplateImportCandidate[] = selectedTypes.flatMap(
                (type) => {
                    const questionnaire =
                        createdByType.get(type)?.cycleQuestionnaire ??
                        cycleEvaluationByType[type]?.cycleQuestionnaire;
                    const chosenTemplateId = selection.templateIds[type];

                    if (
                        !questionnaire?.id ||
                        !chosenTemplateId ||
                        chosenTemplateId === getPersistedTemplateId(questionnaire)
                    ) {
                        return [];
                    }

                    return [
                        { type, questionnaireId: questionnaire.id, templateId: chosenTemplateId },
                    ];
                },
            );

            const importOutcomes = await Promise.allSettled(
                templateImportCandidates.map(({ questionnaireId, templateId }) =>
                    importTemplateInCycleQuestionnaire({ questionnaireId, templateId }),
                ),
            );

            const typesWithDeletedTemplate = importOutcomes.flatMap((outcome, index) =>
                outcome.status === 'rejected' && isDeletedTemplateImportFailure(outcome.reason)
                    ? [templateImportCandidates[index].type]
                    : [],
            );

            if (typesWithDeletedTemplate.length > 0) {
                onTemplateImportFailed(typesWithDeletedTemplate);
                showWarningToast({ message: t(TEMPLATE_NO_LONGER_AVAILABLE) });
            }

            const otherFailure = importOutcomes.find(
                (outcome): outcome is PromiseRejectedResult =>
                    outcome.status === 'rejected' &&
                    !isDeletedTemplateImportFailure(outcome.reason),
            );

            if (otherFailure) {
                throw otherFailure.reason;
            }

            if (typesWithDeletedTemplate.length > 0) {
                return false;
            }

            // A draft's resume point moves on; a launched cycle has none left to move.
            if (!isLaunched) {
                await updateCycleEvaluationGroup({
                    id: cycleEvaluationGroupId,
                    payload: buildCycleEvaluationGroupUpdatePayload({
                        group,
                        currentStep: selectionIncludesFeedback360(selection)
                            ? CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS
                            : CycleEvaluationGroupSteps.DEADLINES,
                    }),
                });
            }
        } catch (error) {
            handleError({ error });

            return false;
        }

        return true;
    }, [
        isReadOnly,
        selection,
        typesWithUnavailableTemplate,
        typesWithUnresolvableTemplate,
        t,
        group,
        cycleEvaluationGroupId,
        cycleEvaluationByType,
        createCycleEvaluationForGroup,
        updateCycleEvaluation,
        importTemplateInCycleQuestionnaire,
        updateCycleEvaluationGroup,
        onTemplateImportFailed,
        handleError,
    ]);
};
